import { useEffect, useState } from 'react';
import useTranslation from 'next-translate/useTranslation';

import { Button, Modal } from '@/components/ui';
import useGeolocation from '@/hooks/useGeoLocation';
import { validCountries } from '@/constants/base';

import GoogleMap from './GoogleMap/GoogleMap';

import styles from './AddressModal.module.scss';

const center = {
  lat: 40.409264,
  lng: 49.867092,
};

type Address = {
  address_coordinates: string;
  address: string;
};

type Props = {
  isActive: boolean;
  setIsActive: (isActive?: boolean) => void;
  handleNext?: (address: Address) => void;
  formik: any;
};

const AddressModal = ({
  isActive = false,
  setIsActive = () => null,
  handleNext,
  formik,
}: Props) => {
  const { t } = useTranslation();

  const { latitude, longitude, error } = useGeolocation();

  const [address, setAddress] = useState<Address | undefined>(undefined);

  const [mapCenter, setMapCenter] = useState<{ lat: number; lng: number }>(
    center,
  );

  const [isDisabled, setIsDisabled] = useState(false);

  useEffect(() => {
    if (!error && latitude && longitude) {
      setMapCenter({ lat: Number(latitude), lng: Number(longitude) });
    }
  }, [latitude, longitude, error]);

  useEffect(() => {
    if (
      !validCountries.some((country) => address?.address?.includes(country))
    ) {
      setIsDisabled(true);
    } else {
      setIsDisabled(false);
    }
  }, [address]);

  const handleModalNext = (address: Address) => {
    if (handleNext) {
      handleNext(address);
    }
    formik.setFieldValue('address_coordinates', address);
    formik.setFieldValue('address', address?.address);
    formik.setFieldValue('pickup_point_id', null);
    formik.setFieldValue('validPoint', true);
    setIsActive(false);
  };

  return (
    <div itemScope itemType='https://schema.org/GeoCoordinates'>
      {isActive && (
        <Modal
          active={isActive}
          setActive={setIsActive}
          className={styles.addressModalWrapper}
          classNameInner={styles.addressModal}
        >
          <h2 className={styles.addressModalTitle}>
            {t('device:address.ChooseDeliveryAddress')}
          </h2>
          <div className={styles.contentWrapper}>
            <GoogleMap
              customMarker={'/assets/map/AddressMarker.svg'}
              center={mapCenter}
              setMapCenter={setMapCenter}
              address={address}
              setAddress={setAddress}
            />
          </div>
          <Button
            className={styles.nextButton}
            onClick={() => {
              handleModalNext(address);
            }}
            disabled={!address || isDisabled}
          >
            {t('common:Continue')}
          </Button>
        </Modal>
      )}
    </div>
  );
};

export default AddressModal;
